import Article from "./article";
import Center from "../../components/center";
import style from "./element-query-polyfill.scss";
import GoogleAdsense from "../../components/google-adsense";
import namedRoutes from "../../settings/routes";
import { lookupByUrl } from "../../settings/route-data-lookup";

const headerInfo = lookupByUrl(
  namedRoutes.article("2013/06/25", "element-query-polyfill")
);

const ArticleContent = () => (
  <Article headerInfo={headerInfo}>
    <Center>
      <h1>{headerInfo.title}</h1>

      <p class="entry">
        Responsive Web design has transformed how websites are designed and
        built. It has inspired us to think beyond device classifications and to
        use media queries to adapt a layout to the browser’s viewport size.
        This, however, deviates from the hierarchical structure of CSS and
        characterizes elements relative to the viewport, instead of to their{" "}
        <strong>container</strong>.
      </p>

      <p>
        Extensive use of media queries might be the answer for today, but it is
        not a viable long-term solution. Media queries do not allow for reusable
        modules that adapt based on their <strong>containers’</strong> size...
      </p>

      <div class="button-list">
        <a
          class="button"
          href="https://www.smashingmagazine.com/2013/06/media-queries-are-not-the-answer-element-query-polyfill/"
        >
          Read Full Article
        </a>{" "}
        <a
          class="button secondary"
          href="https://github.com/tysonmatanich/elementQuery"
        >
          View on GitHub
        </a>
      </div>

      <GoogleAdsense type="Homepage Feed" />
    </Center>
    <Center>
      <h2>Important Update: CSS Container Queries</h2>

      <p>
        As element queries got closer to implementation, they received a new
        name that better reflected their functionality. Element queries are now{" "}
        <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Container_Queries">
          CSS Container Queries
        </a>
        . In September 2022 browser support landed in Chrome, Edge, and Safari.
        Opera followed suit a few months later, then on Valentine's Day 2023
        Firefox joined the club and showed us all some love.
      </p>

      <p>CSS Container Queries are supported in the following browsers:</p>
      <ul>
        <li>Chrome 106 - Sep 27, 2022</li>
        <li>Edge 106 - Sep 15, 2022</li>
        <li>Safari 16.0 - September 12, 2022</li>
        <li>Firefox 110 - February 14, 2023</li>
        <li>Opera 94 - December 15, 2022</li>
        <li>Chrome for Android 109 - January 10, 2023</li>
        <li>Safari on iOS 16.0 - September 12, 2022</li>
        <li>Opera Mobile 73 - February 2, 2023</li>
        <li>Android Browser 109 - January 10, 2023</li>
      </ul>

      <p class="caption">
        View up to date support on{" "}
        <a href="https://caniuse.com/css-container-queries">Can I Use</a>. The
        list above was updated in February 2023.
      </p>

      <p>
        It's exciting to see how much progress has occurred since I published my
        article almost 10 years ago. Thank you to everyone that helped make this
        possible!
      </p>

      <h3>Further reading</h3>
      <ul>
        <li>
          <a href="https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Container_Queries">
            MDN - CSS Container Queries
          </a>
        </li>
        <li>
          <a href="https://caniuse.com/css-container-queries">
            Can I Use - CSS Container Queries
          </a>{" "}
          (up to date browser support)
        </li>
        <li>
          <a href="https://www.w3.org/TR/css-contain-3/">
            CSS Containment Module Level 3
          </a>{" "}
          (Official W3C spec)
        </li>
        <li>
          <a href="/2014/05/01/inline-block-for-vertically-aligned-grids/">
            Inline-block for vertically aligned grids
          </a>
        </li>
      </ul>
    </Center>
    <Center>
      <h2>Original Article</h2>
      <p class="caption">
        Published to{" "}
        <a href="https://www.smashingmagazine.com/2013/06/media-queries-are-not-the-answer-element-query-polyfill/">
          Smashing Magazine
        </a>
        , but include here as an archive.
      </p>
      <section aria-label="Quick summary" class={style.articleSummary}>
        <span class={style.summaryHeading} aria-hidden="true" />
        Extensive use of media queries is&nbsp;not a viable long-term solution.
        Media queries do not allow for reusable modules that adapt based on
        their containers’ size. Responsive Web design has inspired us to think
        beyond device classifications and to use media queries to adapt a layout
        to the browser’s viewport size. But this deviates from the hierarchical
        structure of CSS and characterizes elements relative to the viewport,
        instead of to their container.
      </section>
      <div>
        <p>
          Responsive Web design has transformed how websites are designed and
          built. It has inspired us to think beyond device classifications and
          to use media queries to adapt a layout to the browser’s viewport size.
          This, however, deviates from the hierarchical structure of CSS and
          characterizes elements relative to the viewport, instead of to their
          container.
        </p>
        <p>
          Extensive use of media queries might be the answer for today, but it
          is <strong>not a viable long-term solution</strong>. Media queries do
          not allow for reusable modules that adapt based on their containers’
          size.
        </p>
        <h2 id="what-is-responsive-web-design">
          What Is Responsive Web Design?&nbsp;
          <a class="anchor" href="#what-is-responsive-web-design">
            #
          </a>
        </h2>
        <p>
          Responsive Web design is not limited to a set of technologies; rather,
          it is a different approach to designing and building websites. I, like
          many, took{" "}
          <a href="https://alistapart.com/article/responsive-web-design">
            Ethan’s words
          </a>{" "}
          about responsive Web design too literally and overlooked the essence
          of what was being said:
        </p>
        <blockquote>
          "Fluid grids, flexible images, and media queries are the three
          technical ingredients for responsive web design, but it also requires
          a different way of thinking."
        </blockquote>
        <p>
          We have accomplished great things while embracing the stated
          “technical ingredients” for responsive Web design, but we have much
          room for growth when it comes to a “different way of thinking.”
          Thinking differently should affect not only how we design and build
          our websites, but also how we design and build the tools and
          technologies that our websites are founded on.
        </p>
        <h2 id="modular-design">
          Modular Design&nbsp;
          <a class="anchor" href="#modular-design">
            #
          </a>
        </h2>
        <p>
          When I learned about how media queries could be used in responsive Web
          design, I was excited by the possibilities. However, it was not long
          before I learned of the limitations. Media queries are great for
          adapting layouts to various screen sizes, but terrible for creating{" "}
          <a href="https://daverupert.com/2013/04/responsive-deliverables/">
            modular designs
          </a>
          . Modular CSS is already hard enough, and media queries provide very
          little to no help.{" "}
          <strong>
            Truly modular layouts need to respond to the sizes of containers
          </strong>
          , not just to the viewport’s size. Media queries, however, are based
          on the viewport, rather than an element’s container. There is some
          hope for standard CSS on the horizon, in the form of a{" "}
          <a href="https://www.w3.org/TR/css3-cascade/#all">
            W3C working draft
          </a>
          , by allowing the cascade inheritance to be broken and resetting an
          element to its defaults. But what about media queries?
        </p>
        <h2 id="the-media-hack">
          The @media Hack&nbsp;
          <a class="anchor" href="#the-media-hack">
            #
          </a>
        </h2>
        <p>
          Web developers are masters at taking something created for one purpose
          and using it to accomplish other things. The Web’s history is littered
          with examples of this, and media queries are no exception. Kudos to
          Ian Storm Taylor for writing down his thoughts in the article “
          <a href="https://ianstormtaylor.com/media-queries-are-a-hack/">
            Media Queries Are a Hack
          </a>
          .” Hacks are necessary on the Web to provide desired functionality
          until proper support is achieved, as well as to provide support to
          older browsers. The{" "}
          <a href="https://www.w3.org/TR/css3-mediaqueries/">W3C states</a>, “By
          using media queries, presentations can be tailored to a specific range
          of output devices <strong>without changing the content itself</strong>
          .” The key word here is “can,” but just because you <em>can</em> do
          something, doesn’t mean you <em>should</em>… But do we have any other
          choice?
        </p>
        <h2 id="the-element-query">
          The Element Query&nbsp;
          <a class="anchor" href="#the-element-query">
            #
          </a>
        </h2>
        <p>
          Introducing the element query. An element query is similar to a media
          query in that, if a condition is met, some CSS will be applied.
          Element query conditions (such as <code>min-width</code>,{" "}
          <code>max-width</code>, <code>min-height</code> and{" "}
          <code>max-height</code>) are based on elements, instead of the
          browser. Unfortunately, CSS doesn’t yet support element queries, but
          that shouldn’t stop us from dreaming, hacking and pushing for new
          standards.
        </p>
        <h3 id="conceptual-example">
          Conceptual Example&nbsp;
          <a class="anchor" href="#conceptual-example">
            #
          </a>
        </h3>
        <p>
          Consider the following example, in which the navigation menu should
          become visible when it reaches a minimum width of 500 pixels
          (representing one of many potential syntaxes):
        </p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">nav (min-width: 500px)</span>
                <span class="token punctuation">{" {"}</span>
              </div>
              {"  "}
              <span class="token property">display</span>
              <span class="token punctuation">:</span> block
              <span class="token punctuation">;</span>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>
          Compare this to a media query in which the navigation menu’s
          visibility depends on the viewport’s width and needs to account for
          the padding and other declarations of parent elements:
        </p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token atrule">
                  <span class="token rule">@media</span> all{" "}
                  <span class="token keyword">and</span>{" "}
                  <span class="token punctuation">(</span>
                  <span class="token property">min-width</span>
                  <span class="token punctuation">:</span> 520px
                  <span class="token punctuation">)</span>
                </span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token selector">nav</span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"    "}
                <span class="token property">display</span>
                <span class="token punctuation">:</span> block
                <span class="token punctuation">;</span>
              </div>
              <div>
                {"  "}
                <span class="token punctuation">{"}"}</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>
          Now imagine having to build a modular component that needs to be
          placed in containers of various sizes on a single page. One current
          approach is to provide different theme classes (like{" "}
          <code>.module–large</code>) to trigger CSS in media queries. This,
          however, adds a lot of complications and requires a module to know how
          its parent will react to various viewport widths.
        </p>
        <h3 id="issues-invalid-and-looping-conditions">
          Issues: Invalid And Looping Conditions&nbsp;
          <a class="anchor" href="#issues-invalid-and-looping-conditions">
            #
          </a>
        </h3>
        <p>
          There are several cases in which the CSS of an element query would
          invalidate the element query itself or create recursion. Hopefully,
          the browser would be able to detect these conditions and respond
          appropriately.
        </p>
        <p>Consider the following examples.</p>
        <p>
          Once an element reaches 500 pixels wide, it’s resized to 200 pixels,
          at which point the rule would no longer apply:
        </p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">.element (min-width: 500px)</span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">width</span>
                <span class="token punctuation">:</span> 200px
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>
          Once an element’s width reaches 31.250 ems, its font size would be
          decreased, which changes the definition of the em unit:
        </p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">
                  .element (min-width: 31.250em)
                </span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">font-size</span>
                <span class="token punctuation">:</span> 0.75em
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>
          Once a container’s width reaches 450 pixels, the size of its child
          changes to 400 pixels, which would shrink the size of the container:
        </p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">.container</span>
                <span class="token punctuation">{" { "}</span>
                <span class="token property">float</span>
                <span class="token punctuation">:</span> left
                <span class="token punctuation">;</span>
                <span class="token punctuation">{" }"}</span>
              </div>
              <div>
                <span class="token selector">.child</span>
                <span class="token punctuation">{" { "}</span>
                <span class="token property">width</span>
                <span class="token punctuation">:</span> 500px
                <span class="token punctuation">;</span>
                <span class="token punctuation">{" }"}</span>
              </div>
              <div>
                <span class="token selector">
                  .container (min-width: 450px) &gt; .child
                </span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">width</span>
                <span class="token punctuation">:</span> 400px
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>
          There are many other such examples, but you get the point: Element
          queries are not as simple as we had hoped.
        </p>
        <h2 id="element-query-polyfill">
          Element Query Polyfill&nbsp;
          <a class="anchor" href="#element-query-polyfill">
            #
          </a>
        </h2>
        <p>
          Element queries seem pretty awesome, but they also have some real
          issues. To help sort these out, I’ve written a proof-of-concept
          polyfill. The polyfill has enabled me to understand how a browser
          might react to various conditions. As I got further along, I realized
          that the polyfill could hold real value in the Web community’s debate
          on element queries, and that some developers could even start using
          element queries today.
        </p>
        <p>
          The elementQuery polyfill script is available{" "}
          <a href="https://github.com/tysonmatanich">on GitHub</a> for you to
          use, fork and contribute to.
        </p>

        <h3 id="selector-syntax">
          Selector Syntax&nbsp;
          <a class="anchor" href="#selector-syntax">
            #
          </a>
        </h3>
        <p>
          The syntax used in the previous examples caused limitations, so I
          updated the polyfill to support an attribute selector syntax. The{" "}
          <a href="https://css-tricks.com/attribute-selectors/#rel-space">
            <code>~=</code> attribute selector
          </a>
          checks whether the value is contained in a space-delimited list
          (supported in modern browsers above Internet Explorer 6).
        </p>
        <p>
          The following examples show CSS rules using the syntax required for
          the elementQuery polyfill.
        </p>
        <p>This rule queries itself for a single condition:</p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">header[min-width~="500px"]</span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">background-color</span>
                <span class="token punctuation">:</span> #eee
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>This rule queries itself for multiple conditions:</p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">
                  header[min-width~="500px"][max-width~="800px"]
                </span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">background-color</span>
                <span class="token punctuation">:</span> #eee
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <p>This rule queries a parent for a condition:</p>
        <div class="code-toolbar">
          <pre class="language-css">
            <code class=" language-css">
              <div>
                <span class="token selector">
                  header[min-width~="31.250em"] nav
                </span>
                <span class="token punctuation">{" {"}</span>
              </div>
              <div>
                {"  "}
                <span class="token property">clear</span>
                <span class="token punctuation">:</span> both
                <span class="token punctuation">;</span>
              </div>
              <div>
                <span class="token punctuation">{"}"}</span>
              </div>
            </code>
          </pre>
        </div>
        <h3 id="how-it-works">
          How It Works&nbsp;
          <a class="anchor" href="#how-it-works">
            #
          </a>
        </h3>
        <p>
          Unfortunately, the elementQuery polyfill requires JavaScript and the{" "}
          <a href="https://sizzlejs.com/">Sizzle</a> selector engine (which is
          embedded in jQuery). When the document object model (DOM) is ready,
          elementQuery scans the <code>document.styleSheets</code> collection
          for any CSS rules that use elementQuery. When it finds a match, it
          extracts the following information:
        </p>
        <ul>
          <li>
            <strong>Selector</strong>. Such as <code>header</code>,{" "}
            <code>ul &gt; li.class</code>
          </li>
          <li>
            <strong>Query type</strong> <code>min-width</code>,{" "}
            <code>max-width</code>,<code>min-height</code>,{" "}
            <code>max-height</code>
          </li>
          <li>
            <strong>Query value</strong>. Such as <code>500px</code>,{" "}
            <code>31.250em</code>
          </li>
        </ul>
        <p>
          elementQuery then uses this information to add or remove attributes
          from elements that match the given selector and query condition.
        </p>
        <h3 id="expanded-support">
          Expanded Support&nbsp;
          <a class="anchor" href="#expanded-support">
            #
          </a>
        </h3>
        <p>
          Most browsers, but not Internet Explorer, don’t provide access to the
          contents of cross-domain style sheets, which causes issues when CSS
          files are served from a content delivery network. Additionally,
          parsing style sheets takes time (not much, though). So, I created two
          branches for elementQuery:{" "}
          <a href="https://github.com/tysonmatanich/elementQuery">master</a> and{" "}
          <a href="https://github.com/tysonmatanich/elementQuery/tree/prod">
            prod
          </a>
          . The master branch includes the code for extracting the necessary
          elementQuery information (selector, query type, query value), and it
          also provides a <code>selectors()</code> function to export the
          information. The prod branch requires the information to be declared
          in JavaScript, which avoids the cross-domain file issue and the time
          required to parse the style sheets.
        </p>
        <p>
          Here is an example of how to export elementQuery information using the
          master branch:
        </p>
        <div class="code-toolbar">
          <pre class="language-javascript">
            <code class=" language-javascript">
              console<span class="token punctuation">.</span>
              <span class="token function">log</span>
              <span class="token punctuation">(</span>
              <span class="token constant">JSON</span>
              <span class="token punctuation">.</span>
              <span class="token function">stringify</span>
              <span class="token punctuation">(</span>elementQuery
              <span class="token punctuation">.</span>
              <span class="token function">selectors</span>
              <span class="token punctuation">(</span>
              <span class="token punctuation">)</span>
              <span class="token punctuation">)</span>
              <span class="token punctuation">)</span>
              <span class="token punctuation">;</span>
            </code>
          </pre>
        </div>
        <p>
          And here is an example of how to import elementQuery information using
          the prod branch:
        </p>
        <div class="code-toolbar">
          <pre class="language-javascript">
            <code class=" language-javascript">
              <span class="token function">elementQuery</span>
              <span class="token punctuation">(</span>
              <span class="token punctuation">{"{"}</span>
              <span class="token string">"header"</span>
              <span class="token operator">:</span>
              <span class="token punctuation">{"{"}</span>
              <span class="token string">"min-width"</span>
              <span class="token operator">:</span>
              <span class="token punctuation">[</span>
              <span class="token string">"500px"</span>
              <span class="token punctuation">,</span>
              <span class="token string">"31.250em"</span>
              <span class="token punctuation">]</span>
              <span class="token punctuation">,</span>
              <span class="token string">"max-width"</span>
              <span class="token operator">:</span>
              <span class="token punctuation">[</span>
              <span class="token string">"800px"</span>
              <span class="token punctuation">]</span>
              <span class="token punctuation">{"}"}</span>
              <span class="token punctuation">{"}"}</span>
              <span class="token punctuation">)</span>
              <span class="token punctuation">;</span>
            </code>
          </pre>
        </div>
        <h3 id="working-examples">
          Working Examples&nbsp;
          <a class="anchor" href="#working-examples">
            #
          </a>
        </h3>
        <p>
          I’ve put together a few working examples on CodePen (using the master
          branch) that you can experiment with or fork. I would love to see what
          other examples people create (which will probably be much cooler than
          mine). Just be sure to tag them with{" "}
          <a href="https://codepen.io/tag/elementquery">#elementquery</a> so
          that others can benefit.
        </p>
        <ul>
          <li>
            <a href="https://codepen.io/tysonmatanich/pen/johpn">Grid</a>:
            nested elements
          </li>
          <li>
            <a href="https://codepen.io/tysonmatanich/pen/wramd">Menu</a>:
            width-based elementQuery
          </li>
          <li>
            <a href="https://codepen.io/tysonmatanich/pen/jIBpJ">Blockquote</a>:
            height-based elementQuery
          </li>
        </ul>
        <h2 id="be-creative">
          Be Creative&nbsp;
          <a class="anchor" href="#be-creative">
            #
          </a>
        </h2>
        <p>
          I didn’t write this just to get you to jump on the element query
          bandwagon, but rather to encourage people to think about how we can
          solve the problems that are limiting our medium. Let’s keep the
          discussion going and make the Web a better place. So, go wild and make
          cool stuff!
        </p>
        <ul>
          <li>
            View{" "}
            <a href="https://github.com/tysonmatanich/elementQuery">
              elementQuery
            </a>{" "}
            on GitHub.
          </li>
        </ul>
        <h3 id="other-resources">
          Other Resources&nbsp;
          <a class="anchor" href="#other-resources">
            #
          </a>
        </h3>
        <ul>
          <li>
            “
            <a href="https://www.matanich.com/2013/06/24/em-values-javascript/">
              Em values in JavaScript
            </a>
            ,” Tyson Matanich
          </li>
          <li>
            “
            <a href="https://filamentgroup.com/lab/element_query_workarounds/">
              Working Around a Lack of Element Queries
            </a>
            ,” Filament Group
          </li>
          <li>
            “<a href="https://www.xanthir.com/b4PR0">Element Queries</a>,” Tab
            Atkins Jr.
          </li>
        </ul>
      </div>
    </Center>
  </Article>
);

export default ArticleContent;
